import { ChevronRight, Lightbulb, ListChecks, Plus, Wallet } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { deleteCosto } from "../../../api";
import type { CostoFamilia, CostoHecho, CostosUnificado } from "../../../types";
import { MobileBottomNav, MobileFab, MobileSheet } from "../../social/mobile/MobileChrome";
import type { CostoPrefill } from "../CostoForm";
import { estadoGcp, etiquetas, filasDelMes, modeloMes, mesesVisibles, prefillDe, type Filtro } from "../costosData";
import { FAMILIAS, fmtClp, fmtClpCorto, mesCorto, mesLargo } from "../costosFormat";
import { CostoFormMobile } from "./CostoFormMobile";

interface Props {
  data: CostosUnificado;
  theme: "light" | "dark";
  onReload: () => Promise<void>;
}

type Pestana = "resumen" | "ahorro" | "detalle";

const SEVERIDAD: Record<string, string> = { alta: "Urgente", media: "Revisar", baja: "Opcional" };

/** Costos en teléfono: una cifra grande arriba, tres pestañas con el pulgar y la carga en una hoja inferior. */
export function CostosMobile({ data, theme, onReload }: Props) {
  const [selected, setSelected] = useState(data.actual);
  const [tab, setTab] = useState<Pestana>("resumen");
  const [filtro, setFiltro] = useState<Filtro>("todas");
  const [form, setForm] = useState<CostoPrefill | null>(null);
  const [fila, setFila] = useState<CostoHecho | null>(null);
  const [confirmaBorrar, setConfirmaBorrar] = useState(false);
  const [faltantesAbierto, setFaltantesAbierto] = useState(false);
  const [aviso, setAviso] = useState<{ texto: string; malo?: boolean } | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tab]);

  useEffect(() => {
    if (!aviso) return;
    const id = window.setTimeout(() => setAviso(null), 3500);
    return () => window.clearTimeout(id);
  }, [aviso]);

  const t = data.totales[selected];
  const m = modeloMes(data, selected);
  const filas = useMemo(() => filasDelMes(data, selected, filtro), [data, selected, filtro]);
  const meses = mesesVisibles(data);
  const maxMes = Math.max(1, ...meses.map((x) => data.totales[x]?.total ?? 0));
  const maxDelta = Math.max(1, ...m.suben.map((s) => s.delta));
  const accionables = data.recomendaciones.filter((r) => r.severidad !== "baja").length || data.recomendaciones.length;

  const abrirForm = (p: CostoPrefill) => { setFila(null); setFaltantesAbierto(false); setForm(p); };
  const guardado = async (texto: string) => { setForm(null); setAviso({ texto }); await onReload(); };
  const cerrarFila = () => { setFila(null); setConfirmaBorrar(false); };
  const borrar = async () => {
    if (!fila?.id) return;
    try { await deleteCosto(fila.id); cerrarFila(); setAviso({ texto: "Costo eliminado" }); await onReload(); }
    catch (e: any) { cerrarFila(); setAviso({ texto: e?.message || "No se pudo eliminar.", malo: true }); }
  };

  const valorFamilia = (f: CostoFamilia): { valor: string; sub: string; alerta?: boolean } => {
    if (f === "gcp") return t?.gcp_sin_datos ? { valor: "Sin datos", sub: estadoGcp(data, selected), alerta: true } : { valor: fmtClp(t?.gcp), sub: estadoGcp(data, selected) };
    if (f === "manual") return { valor: t?.manual || m.hayManual ? fmtClp(t?.manual) : "Sin cargar", sub: m.esActual && data.faltantes.length > 0 ? `${data.faltantes.length} gastos esperados sin cargar` : "Cargado a mano" };
    return { valor: fmtClp(t?.ia), sub: "Cuentas cargadas y API directa" };
  };

  const compuesto = FAMILIAS.map((f) => ({ ...f, v: t?.[f.key] ?? 0 })).filter((x) => x.v > 0);
  const proyeccion = data.forecast.total;
  const avance = m.esActual && proyeccion > 0 ? Math.min(100, (m.total / proyeccion) * 100) : 0;

  const resumen: ReactNode = (
    <>
      <section className="cm-hero" aria-label={`Total de ${mesLargo(selected)}`}>
        <span className="cm-hero-label">{mesLargo(selected)}{m.esActual ? " · en curso" : ""}</span>
        <strong className="cm-hero-value">{t?.total ? fmtClp(t.total) : t?.gcp_sin_datos ? "Sin datos" : fmtClp(0)}</strong>
        <div className="cm-hero-chips">
          {m.deltaPct != null && <span className={`cm-pill ${m.deltaPct > 0 ? "is-bad" : "is-good"}`}>{m.deltaPct > 0 ? "▲" : "▼"} {Math.abs(m.deltaPct).toFixed(0)} % vs {mesCorto(m.previo!)}</span>}
          {m.gcpIncompleto && <span className="cm-pill is-warn">GCP incompleto</span>}
        </div>
        {compuesto.length > 0 && (
          <div className="cm-compose" aria-hidden="true">
            {compuesto.map((x) => <i key={x.key} className={`cst-${x.key}`} style={{ flexGrow: x.v }} />)}
          </div>
        )}
        {m.esActual && (
          <div className="cm-forecast">
            <div className="cm-forecast-row"><span>Proyección a fin de mes</span><strong>{fmtClp(proyeccion)}</strong></div>
            {proyeccion > m.total * 1.02 && <div className="cm-track" aria-hidden="true"><i style={{ width: `${avance}%` }} /></div>}
            <span className="cm-forecast-sub">
              {data.forecast.completo ? `Día ${data.forecast.dias_transcurridos} de ${data.forecast.dias_mes}` : `No incluye ${data.forecast.gcp == null ? "GCP" : "IA"}: sin fuente`}
            </span>
          </div>
        )}
      </section>

      <nav className="cm-rail" aria-label="Mes">
        {meses.map((x) => {
          const tt = data.totales[x];
          const total = tt?.total ?? 0;
          return (
            <button key={x} type="button" className={`cm-rail-item${x === selected ? " is-on" : ""}`} aria-pressed={x === selected} onClick={() => setSelected(x)}>
              <span className="cm-rail-bars" aria-hidden="true">
                {total > 0 ? FAMILIAS.map((f) => (tt[f.key] > 0 ? <i key={f.key} className={`cst-${f.key}`} style={{ height: `${Math.max(3, (tt[f.key] / maxMes) * 100)}%` }} /> : null)) : <i className="is-empty" />}
              </span>
              <span className="cm-rail-month">{mesCorto(x)}{x === data.actual ? " •" : ""}</span>
              <span className="cm-rail-amount">{total > 0 ? fmtClpCorto(total) : "—"}</span>
            </button>
          );
        })}
      </nav>

      <section className="cm-group" aria-label="Por familia">
        {FAMILIAS.map((f) => {
          const x = valorFamilia(f.key);
          return (
            <button key={f.key} type="button" className="cm-row" onClick={() => { setFiltro(f.key); setTab("detalle"); }}>
              <i className={`cm-dot cst-${f.key}`} aria-hidden="true" />
              <span className="cm-row-main"><strong>{f.label}</strong><small className={x.alerta ? "is-warn" : ""}>{x.sub}</small></span>
              <span className="cm-row-value">{x.valor}</span>
              <ChevronRight size={18} aria-hidden="true" />
            </button>
          );
        })}
        {m.esActual && data.faltantes.length > 0 && (
          <button type="button" className="cm-row cm-row-action" onClick={() => setFaltantesAbierto(true)}>
            <i className="cm-dot cm-dot-plus" aria-hidden="true"><Plus size={14} /></i>
            <span className="cm-row-main"><strong>Cargar gastos que faltan</strong><small>{data.faltantes.map((f) => f.nombre).slice(0, 3).join(", ")}…</small></span>
            <ChevronRight size={18} aria-hidden="true" />
          </button>
        )}
      </section>

      {m.suben.length > 0 && (
        <section className="cm-card" aria-label="Qué sube más">
          <h3 className="cm-h">Qué sube más{m.previo ? ` desde ${mesCorto(m.previo)}` : ""}</h3>
          {m.suben.slice(0, 3).map((s) => (
            <div key={`${s.familia}-${s.servicio}`} className="cm-sube">
              <div><span>{s.servicio}</span><strong>+{fmtClpCorto(s.delta)}{s.pct != null ? ` · ${s.pct.toFixed(0)} %` : " · nuevo"}</strong></div>
              <i className={`cst-${s.familia}`} style={{ width: `${Math.max(6, (s.delta / maxDelta) * 100)}%` }} />
            </div>
          ))}
        </section>
      )}
    </>
  );

  const ahorro: ReactNode = (
    <>
      <p className="cm-lead">{data.recomendaciones.length === 0 ? "Sin acciones con los datos actuales." : `${data.recomendaciones.length} ${data.recomendaciones.length === 1 ? "acción" : "acciones"} para bajar el gasto, de la más urgente a la menos.`}</p>
      {data.recomendaciones.map((r) => (
        <article key={r.id} className={`cm-rec is-${r.severidad}`}>
          <div className="cm-rec-top"><span className={`cm-tag is-${r.severidad}`}>{SEVERIDAD[r.severidad]}</span>{r.ahorro_clp != null && <span className="cm-rec-monto">{fmtClpCorto(r.ahorro_clp)}</span>}</div>
          <strong>{r.titulo}</strong>
          <p>{r.detalle}</p>
          <p className="cm-rec-accion">{r.accion}</p>
        </article>
      ))}
      {data.consultas_bq.length > 0 && (
        <>
          <h3 className="cm-h cm-h-lone">Consultas de BigQuery que más escanean</h3>
          {data.consultas_bq.map((c) => (
            <article key={c.firma} className="cm-query">
              <div className="cm-query-top"><strong>{c.pct.toFixed(0)} %</strong><span>{c.tib.toFixed(2)} TiB{c.costo_clp != null ? ` · ≈ ${fmtClpCorto(c.costo_clp)}` : ""}</span></div>
              <div className="cm-track" aria-hidden="true"><i style={{ width: `${Math.min(100, c.pct)}%` }} /></div>
              <code>{c.firma}</code>
              <small>{c.veces} ejecuciones en 30 días</small>
            </article>
          ))}
        </>
      )}
    </>
  );

  const segmentos: [Filtro, string][] = [["todas", "Todas"], ...FAMILIAS.map((f): [Filtro, string] => [f.key, f.short])];
  const detalle: ReactNode = (
    <>
      <div className="cm-seg" role="tablist" aria-label="Familia">
        {segmentos.map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={filtro === k} onClick={() => setFiltro(k)}>{l}</button>)}
      </div>
      {filas.length === 0 ? (
        <p className="cm-empty">Sin registros{filtro !== "todas" ? " en esta familia" : ""} para {mesLargo(selected)}.</p>
      ) : (
        <section className="cm-group">
          {filas.map((h, i) => (
            <button key={`${h.servicio}-${i}`} type="button" className={`cm-row${h.en_total ? "" : " is-attr"}`} onClick={() => setFila(h)}>
              <i className={`cm-dot cst-${h.familia}`} aria-hidden="true" />
              <span className="cm-row-main"><strong>{h.servicio}</strong><small>{etiquetas(h).join(" · ")}</small></span>
              <span className="cm-row-value">{fmtClp(h.monto_clp)}</span>
            </button>
          ))}
        </section>
      )}
    </>
  );

  return (
    <div className="sbx cst-m" data-theme={theme}>
      <header className="cm-top">
        <span className="cm-eyebrow">Finanzas · Dirección</span>
        <h1>{tab === "resumen" ? "Costos" : tab === "ahorro" ? "Ahorro" : "Detalle"}</h1>
      </header>
      {aviso && <div className={`cm-toast${aviso.malo ? " is-bad" : ""}`} role="status">{aviso.texto}</div>}

      {tab === "resumen" ? resumen : tab === "ahorro" ? ahorro : (
        <>
          <nav className="cm-rail cm-rail-slim" aria-label="Mes">
            {meses.map((x) => <button key={x} type="button" className={`cm-month${x === selected ? " is-on" : ""}`} aria-pressed={x === selected} onClick={() => setSelected(x)}>{mesCorto(x)}</button>)}
          </nav>
          {detalle}
        </>
      )}

      <MobileBottomNav
        theme={theme}
        active={tab}
        onSelect={(id) => setTab(id as Pestana)}
        items={[
          { id: "resumen", label: "Resumen", icon: <Wallet size={22} /> },
          { id: "ahorro", label: "Ahorro", icon: <Lightbulb size={22} />, badge: accionables || undefined },
          { id: "detalle", label: "Detalle", icon: <ListChecks size={22} /> },
        ]}
      />
      <MobileFab label="Cargar" icon={<Plus size={22} />} theme={theme} onClick={() => abrirForm({})} />

      {form && <CostoFormMobile mes={selected} prefill={form} theme={theme} onSaved={guardado} onClose={() => setForm(null)} />}

      <MobileSheet open={faltantesAbierto} theme={theme} title="Gastos que faltan" onClose={() => setFaltantesAbierto(false)}>
        <p className="cm-hint">Toca uno para cargar su monto de {mesLargo(selected)}.</p>
        {data.faltantes.map((f) => (
          <button key={f.nombre} type="button" className="sbx-m-option" onClick={() => abrirForm({ categoria: f.categoria, concepto: f.nombre })}>
            <span>{f.nombre}<small>{f.categoria}</small></span><Plus size={20} aria-hidden="true" />
          </button>
        ))}
      </MobileSheet>

      <MobileSheet
        open={!!fila}
        theme={theme}
        title={fila?.servicio ?? ""}
        onClose={cerrarFila}
        footer={fila?.fuente === "manual" && fila.id ? (
          <>
            <button type="button" className={`sbx-m-action is-quiet${confirmaBorrar ? " is-danger" : ""}`} onClick={() => (confirmaBorrar ? void borrar() : setConfirmaBorrar(true))}>{confirmaBorrar ? "Confirmar eliminación" : "Eliminar"}</button>
            {!confirmaBorrar && <button type="button" className="sbx-m-action" onClick={() => abrirForm(prefillDe(fila))}>Editar</button>}
          </>
        ) : undefined}
      >
        {fila && (
          <div className="cm-detail">
            <strong className="cm-detail-amount">{fmtClp(fila.monto_clp)}</strong>
            <dl>
              <div><dt>Familia</dt><dd>{FAMILIAS.find((f) => f.key === fila.familia)?.label}</dd></div>
              <div><dt>Proveedor</dt><dd>{fila.proveedor}</dd></div>
              <div><dt>Origen</dt><dd>{etiquetas(fila).join(" · ")}</dd></div>
              {fila.llamadas != null && <div><dt>{fila.servicio.startsWith("Bots") ? "Conversaciones" : "Llamadas"}</dt><dd>{fila.llamadas}</dd></div>}
              {fila.usd != null && <div><dt>Estimado en USD</dt><dd>US$ {fila.usd.toFixed(2)}</dd></div>}
              <div><dt>Mes</dt><dd>{mesLargo(fila.mes)}</dd></div>
            </dl>
            {!fila.en_total && <p className="cm-hint">Este monto ya lo cobra GCP: se muestra para atribuir el gasto, no se suma al total.</p>}
          </div>
        )}
      </MobileSheet>
    </div>
  );
}
